import { AuditLogsListResponse } from '@lmc/ts-common'
import {
  ColumnContainer,
  RowContainer,
  StrikethroughText,
  SubText,
} from '../components/styledComponents'
import { Text } from '@lmc/ui-kit/src/general'
import { AuditLogsCategory } from '@lmc/ts-common/lib/dtos/AuditLogs'
import React from 'react'
import dayjs from 'dayjs'
import { getNominative } from 'common/utils'
import { TFunction } from 'react-i18next'
import { reportingConstants } from 'locale/reporting'
import { RangeValue } from 'types/shared'

export type AuditTrailFilters = {
  rangePicker?: RangeValue<dayjs.Dayjs>
  agentId?: string[]
  pagination?: { page: number; sort: 1 | -1 }
  category?: AuditLogsCategory
}

export type AuditTableDataSource = {
  dateTime?: string
  author?: string
  authorId?: string
  role?: string
  event?: string
  details?: {}
  entities?: AuditLogsListResponse['entities']
}

export const actionMap: { [key: string]: string } = {
  create: 'created',
  put: 'updated',
  patch: 'updated',
  delete: 'deleted',
  read: 'read',
}

const isISODateString = (str: string) => {
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/
  return isoDatePattern.test(str)
}

export const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss'

const renderChanges = (value: any) => {
  if (typeof value === 'boolean') {
    return value.toString()
  }

  if (typeof value === 'string' && isISODateString(value)) {
    return dayjs(value).format(dateTimeFormat)
  }

  return value
}

const canRender = (obj: {}) => {
  return (typeof obj === 'object' && obj !== null) || Array.isArray(obj)
}

export const getDetails = (
  t: TFunction<string, undefined>,
  auditLog: AuditLogsListResponse,
  carePlanNominative: string,
  residentNominative: string
): JSX.Element => {
  if (auditLog.description) {
    return <Text>{auditLog.description}</Text>
  }

  if (auditLog.action === 'error') {
    return (
      <Text>
        {t(reportingConstants.ERROR_TRIGGERED)}
        {auditLog.agent?.name || 'user'}
      </Text>
    )
  }

  if (auditLog.category === AuditLogsCategory.CREATE) {
    const createdObject = auditLog.after?.[0]
    if (createdObject) {
      return (
        <>
          {renderObject(createdObject, carePlanNominative, residentNominative)}
        </>
      )
    }
  }

  const details = auditLog.diff?.[0]
  // for PATCH
  if (details) {
    return (
      <>
        {details?.CREATE?.length > 0 && (
          <>
            <SubText level={2}>{t(reportingConstants.ADDED)}:</SubText>
            {details.CREATE.map((createEvent: { path: string; value: any }) => (
              <ColumnContainer>
                <Text weight="medium">
                  {prettifyObjectAttribute(createEvent?.path)}:
                </Text>
                {canRender(createEvent?.value) ? (
                  renderObject(
                    createEvent?.value,
                    carePlanNominative,
                    residentNominative
                  )
                ) : (
                  <Text>{renderChanges(createEvent?.value?.toString())}</Text>
                )}
              </ColumnContainer>
            ))}
          </>
        )}
        {details?.CHANGE?.length > 0 && (
          <>
            <SubText level={2}>{t(reportingConstants.CHANGED)}:</SubText>
            {details.CHANGE.map(
              (changeEvent: { path: string; oldValue: any; value: any }) => (
                <ColumnContainer>
                  <Text weight="medium">
                    {prettifyObjectAttribute(changeEvent.path)}:
                  </Text>
                  <RowContainer>
                    {canRender(changeEvent?.oldValue) ? (
                      renderObject(
                        changeEvent.oldValue,
                        carePlanNominative,
                        residentNominative
                      )
                    ) : (
                      <>
                        <StrikethroughText>
                          {renderChanges(changeEvent?.oldValue?.toString())}
                        </StrikethroughText>{' '}
                      </>
                    )}
                    <Text>
                      {canRender(changeEvent?.value) ? (
                        renderObject(
                          changeEvent.value,
                          carePlanNominative,
                          residentNominative
                        )
                      ) : (
                        <Text>
                          {renderChanges(changeEvent?.value?.toString())}
                        </Text>
                      )}
                    </Text>
                  </RowContainer>
                </ColumnContainer>
              )
            )}
          </>
        )}
        {details?.REMOVE?.length > 0 && (
          <>
            <SubText level={2}>{t(reportingConstants.REMOVED)}:</SubText>
            {details.REMOVE.map(
              (removeEvent: { path: string; oldValue: any }) => (
                <ColumnContainer>
                  <Text weight="medium">
                    {prettifyObjectAttribute(removeEvent?.path)}:
                  </Text>
                  <RowContainer>
                    {canRender(removeEvent?.oldValue) ? (
                      renderObject(
                        removeEvent.oldValue,
                        carePlanNominative,
                        residentNominative
                      )
                    ) : (
                      <>
                        <StrikethroughText>
                          {renderChanges(removeEvent?.oldValue?.toString())}{' '}
                        </StrikethroughText>{' '}
                      </>
                    )}
                  </RowContainer>
                </ColumnContainer>
              )
            )}
          </>
        )}
      </>
    )
  }

  if (auditLog.category === AuditLogsCategory.DELETE) {
    const entity = auditLog.entities?.[0]
    return (
      <Text>Entity {entity?.name || entity?.what?.toString()} deleted.</Text>
    )
  }

  return <Text>{t(reportingConstants.NO_CHANGES)}</Text>
}

export const getFullEvent = (
  event: string,
  record: AuditTableDataSource,
  carePlanNominative: string,
  residentNominative: string
) => {
  const entity = record.entities?.[0]
  return (
    <ColumnContainer>
      <Text>
        {handleNominative(event, carePlanNominative, residentNominative)}
      </Text>
      {entity && (
        <>
          <Text>
            {handleNominative(
              `${
                entity.name && entity.name !== 'undefined' ? entity.name : ''
              } ${entity.type ? entity.type : ''}`,
              carePlanNominative,
              residentNominative
            )}
          </Text>
          {entity.what && (
            <SubText level={2} key={entity.what}>
              {entity.what}
            </SubText>
          )}
        </>
      )}
    </ColumnContainer>
  )
}

export const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : ''

export const prettifyObjectAttribute = (attribute: string): string => {
  return capitalize(
    attribute
      // Increment numbers (like .0 to .1 for readability)
      .replace(/\.(\d+)/g, (_, num) => `.${parseInt(num, 10) + 1}`)
      // Insert space before capital letters and convert to lower case
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      // Trim spaces and capitalize the first letter
      .trim()
  )
}

const toLowerCaseHyphenated = (str: string) =>
  str.toLowerCase().replace(/\s+/g, '-')

const renderValue = (
  value: any,
  key: string,
  carePlanNominative: string,
  residentNominative: string
) => {
  if ((Array.isArray(value) && value.length === 0) || value == null) {
    return 'empty'
  }

  if (Array.isArray(value)) {
    return value.map((item, index) => (
      <React.Fragment key={`${key}.${index + 1}`}>
        {typeof item === 'object' && item !== null
          ? renderObject(item, carePlanNominative, residentNominative)
          : `${item}`}
      </React.Fragment>
    ))
  }

  if (typeof value === 'object') {
    return renderObject(value, carePlanNominative, residentNominative)
  }

  return renderChanges(value.toString())
}

const renderObject = (
  obj: object,
  carePlanNominative: string,
  residentNominative: string,
  keyPrefix = ''
): JSX.Element | JSX.Element[] => {
  const entries = Object.entries(obj)
  return entries.flatMap(([key, value]) => {
    if (key.startsWith('_') || key.startsWith('formData')) {
      return []
    }

    const prettyKey = prettifyObjectAttribute(key)
    const fullKey = keyPrefix ? `${keyPrefix}.${prettyKey}` : prettyKey

    const renderedValue = handleNominative(
      renderValue(value, key, carePlanNominative, residentNominative),
      carePlanNominative,
      residentNominative
    )

    return (
      <RowContainer key={fullKey}>
        {renderedValue && (
          <Text weight="medium">
            {carePlanNominative && residentNominative
              ? handleNominative(
                  fullKey,
                  carePlanNominative,
                  residentNominative
                )
              : fullKey}
            :{' '}
          </Text>
        )}
        <Text>{renderedValue}</Text>
      </RowContainer>
    )
  })
}

export const handleNominative = (
  characters: string,
  carePlanNominative: string,
  residentNominative: string
) => {
  const preferredResidentNominative = getNominative(
    residentNominative,
    'resident'
  )

  const preferredCarePlanNominative = getNominative(
    carePlanNominative,
    'care plan'
  )

  if (characters.includes('resident')) {
    return characters.replace(
      /resident/g,
      toLowerCaseHyphenated(preferredResidentNominative)
    )
  }

  if (characters.includes('care-plan')) {
    return toLowerCaseHyphenated(
      characters.replace(/care-plan/g, preferredCarePlanNominative)
    )
  }

  if (characters.includes('Resident')) {
    return characters.replace(/Resident/g, preferredResidentNominative)
  }

  if (characters.includes('Care Plan')) {
    return characters.replace(/Care Plan/g, preferredCarePlanNominative)
  }

  if (characters.includes('CarePlan')) {
    return characters.replace(/CarePlan/g, preferredCarePlanNominative)
  }

  return characters
}
